'use client';

import type { CartSnapshot } from '@lib/data/cart';
import {
  getLayoutSnapshot,
  refreshCartSnapshot,
} from '@lib/data/layout-snapshot';
import { useAuth } from '@modules/common/components/auth-provider';
import * as Sentry from '@sentry/nextjs';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { CART_CHANGED_EVENT } from './constants';

const EMPTY_SNAPSHOT: CartSnapshot = {
  cart: null,
  cartCustomerId: undefined,
  hasCustomerAndCart: false,
};

/**
 * Single source of truth for "has a mutation happened" — bumped
 * synchronously (no render dependency) so `getSnapshot()` is always current
 * for a caller that reads it mid-promise-callback (`LayoutDataLoader`'s
 * staleness check), while `useSyncExternalStore` still gives reactive
 * consumers (`CartDropdown`'s auto-open effect) a proper re-render on change.
 * One counter, two ways to read it, instead of a ref/state pair that would
 * need to be kept in sync by hand.
 */
function createMutationVersionStore() {
  let version = 0;
  const listeners = new Set<() => void>();

  return {
    getSnapshot: () => version,
    subscribe: (listener: () => void) => {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    bump: () => {
      version += 1;
      for (const listener of listeners) {
        listener();
      }
    },
  };
}

type CartContextValue = {
  readonly snapshot: CartSnapshot;
  readonly rehydrate: (snapshot: CartSnapshot) => void;
  /** Increments only when `CART_CHANGED_EVENT` fires — i.e. this user just
   * added/updated/removed a cart item — as opposed to `LayoutDataLoader`'s
   * per-navigation `rehydrate()`, which can also change `snapshot.cart`
   * (e.g. the cart changed on another device) but isn't a local action.
   * Lets consumers (`CartDropdown`'s auto-open effect) tell those apart;
   * diffing item count alone can't, since both look identical. */
  readonly mutationVersion: number;
  /** Reads the same counter as `mutationVersion` without subscribing —
   * for callers (`LayoutDataLoader`'s staleness check) that need the
   * current value immediately, mid-promise-callback, rather than waiting
   * for a render. */
  readonly getMutationVersion: () => number;
};

const CartContext = createContext<CartContextValue | null>(null);

export default function CartProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const {
    isLoggedIn,
    resolved: authResolved,
    rehydrate: rehydrateAuth,
  } = useAuth();
  const [snapshot, setSnapshot] = useState<CartSnapshot>(EMPTY_SNAPSHOT);
  const [store] = useState(createMutationVersionStore);
  const mutationVersion = useSyncExternalStore(
    store.subscribe,
    store.getSnapshot,
    store.getSnapshot,
  );

  const rehydrate = useCallback((next: CartSnapshot) => {
    setSnapshot(next);
  }, []);

  // Guards against out-of-order resolution: if two `CART_CHANGED_EVENT`s
  // fire close together (e.g. rapid add-to-cart clicks), each starts its own
  // independent refetch, and an earlier one can resolve after a later one.
  // Only the response for the most-recently-started request is applied.
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    const onCartChanged = () => {
      const requestId = ++latestRequestIdRef.current;
      const applyIfLatest = (apply: () => void) => {
        if (requestId === latestRequestIdRef.current) {
          apply();
        }
      };

      // `isLoggedIn` defaults to `false` before the initial auth check
      // resolves — that default is not a confirmed "logged out", so trusting
      // it here would tag a real customer's cart as guest-only. Fall back to
      // the combined auth+cart fetch (re-deriving both correctly) only for
      // that unresolved window; once resolved, use the cheaper cart-only
      // refresh this path exists for (see `refreshCartSnapshot`'s comment).
      const refreshed = authResolved
        ? refreshCartSnapshot(isLoggedIn).then((next) => {
            applyIfLatest(() => rehydrate(next));
          })
        : getLayoutSnapshot().then(({ auth, cart }) => {
            applyIfLatest(() => {
              rehydrateAuth(auth);
              rehydrate(cart);
            });
          });

      refreshed
        .then(() => {
          // Every dispatch is a genuine local mutation regardless of fetch
          // ordering, so this stays unconditional — `CartDropdown`'s
          // auto-open should fire for each real action, not just the one
          // whose content write happened to win the race above. `bump()`
          // updates the store's own counter synchronously, so a caller
          // reading `getMutationVersion()` mid-callback (`LayoutDataLoader`)
          // never observes a stale value — it's the same counter
          // `useSyncExternalStore` subscribes reactive consumers to.
          store.bump();
        })
        .catch((error) => {
          Sentry.captureException(error, {
            extra: { context: 'CartProvider:onCartChanged' },
          });
        });
    };

    window.addEventListener(CART_CHANGED_EVENT, onCartChanged);
    return () => window.removeEventListener(CART_CHANGED_EVENT, onCartChanged);
  }, [rehydrate, rehydrateAuth, isLoggedIn, authResolved, store]);

  const value = useMemo<CartContextValue>(
    () => ({
      snapshot,
      rehydrate,
      mutationVersion,
      getMutationVersion: store.getSnapshot,
    }),
    [snapshot, rehydrate, mutationVersion, store],
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart(): CartContextValue {
  const ctx = useContext(CartContext);
  if (ctx === null) {
    // Provider isn't mounted on a given route (e.g. checkout) — return a
    // benign empty default so consumers don't have to null-check.
    return {
      snapshot: EMPTY_SNAPSHOT,
      rehydrate: () => undefined,
      mutationVersion: 0,
      getMutationVersion: () => 0,
    };
  }
  return ctx;
}
